import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';

declare const $: any;

type CheckoutFormShape = {
  cartTitle: string;
  itemListTitle: string;
  checkoutSideTitle: string;
  shippingEnabled: boolean;
  footerEnabled: boolean;
  footerCols: {
    col1: { title: string; subtitle: string };
    col2: { title: string; subtitle: string };
    col3: { title: string; subtitle: string };
    col4: { title: string; subtitle: string };
  };
};

export const CHECKOUT_DEFAULTS: CheckoutFormShape = {
  cartTitle: 'Carrito de Compras',
  itemListTitle: 'Lista de artículos',
  checkoutSideTitle: 'Finalizar la compra',
  shippingEnabled: true,
  footerEnabled: true,
  footerCols: {
    col1: { title: 'Envíos a todo el país', subtitle: '' },
    col2: { title: 'Pago seguro', subtitle: '100% pago seguro' },
    col3: { title: 'Garantía de devolución', subtitle: 'Si tiene algún problema con el artículo' },
    col4: { title: 'Soporte', subtitle: 'Te brindamos atención personalizada' },
  }
};

@Component({
  selector: 'app-checkout-template-editor',
  templateUrl: './checkout-template-editor.component.html',
  styleUrls: ['./checkout-template-editor.component.scss']
})
export class CheckoutTemplateEditorComponent implements OnInit {
  form: FormGroup;
  loading = false;
  showErrors = false;

  CHECKOUT_DEFAULTS = CHECKOUT_DEFAULTS;
  private dbSnapshot: CheckoutFormShape | null = null;

  constructor(
    private fb: FormBuilder,
    private lambdaService: LambdaService,
    private themingService: ThemingService
  ) {
    this.form = this.fb.group({
      useDefaults: [true],
      cartTitle: [CHECKOUT_DEFAULTS.cartTitle, [Validators.required, Validators.maxLength(255)]],
      itemListTitle: [CHECKOUT_DEFAULTS.itemListTitle, [Validators.required, Validators.maxLength(255)]],
      checkoutSideTitle: [CHECKOUT_DEFAULTS.checkoutSideTitle, [Validators.required, Validators.maxLength(255)]],

      shippingEnabled: [CHECKOUT_DEFAULTS.shippingEnabled],
      footerEnabled: [CHECKOUT_DEFAULTS.footerEnabled],
      footerCols: this.fb.group({
        col1: this.fb.group({
          title: [CHECKOUT_DEFAULTS.footerCols.col1.title, [Validators.maxLength(255)]],
          subtitle: [CHECKOUT_DEFAULTS.footerCols.col1.subtitle],
        }),
        col2: this.fb.group({
          title: [CHECKOUT_DEFAULTS.footerCols.col2.title, [Validators.maxLength(255)]],
          subtitle: [CHECKOUT_DEFAULTS.footerCols.col2.subtitle],
        }),
        col3: this.fb.group({
          title: [CHECKOUT_DEFAULTS.footerCols.col3.title, [Validators.maxLength(255)]],
          subtitle: [CHECKOUT_DEFAULTS.footerCols.col3.subtitle],
        }),
        col4: this.fb.group({
          title: [CHECKOUT_DEFAULTS.footerCols.col4.title, [Validators.maxLength(255)]],
          subtitle: [CHECKOUT_DEFAULTS.footerCols.col4.subtitle],
        }),
      })
    });
  }

  ngOnInit(): void {
    this.loading = true;

    this.lambdaService.getCheckoutTemplate(this.themingService.shopId)
      .then((res: any) => {
        const row = Array.isArray(res?.data) ? res.data[0] : res?.data;

        if (row) {
          this.dbSnapshot = this.dbRowToForm(row);
          const useDefaults = row.use_defaults === 1;

          if (useDefaults) {
            this.form.patchValue({ useDefaults: true });
            this.form.disable({ emitEvent: false });
            this.form.get('useDefaults')?.enable({ emitEvent: false });
          } else {
            this.form.patchValue({ ...this.dbSnapshot, useDefaults: false });
            this.form.enable({ emitEvent: false });
          }
        } else {
          this.form.patchValue({ ...CHECKOUT_DEFAULTS, useDefaults: true });
          this.form.disable({ emitEvent: false });
          this.form.get('useDefaults')?.enable({ emitEvent: false });
        }
      })
      .catch(err => console.error('GET error', err))
      .finally(() => this.loading = false);


    this.form.get('useDefaults')?.valueChanges.subscribe((useDefaults: boolean) => {
      if (useDefaults) {

        this.form.disable({ emitEvent: false });
        this.form.get('useDefaults')?.enable({ emitEvent: false });
      } else {

        const base = this.dbSnapshot ?? CHECKOUT_DEFAULTS;
        this.form.patchValue({
          cartTitle: base.cartTitle,
          itemListTitle: base.itemListTitle,
          checkoutSideTitle: base.checkoutSideTitle,
          shippingEnabled: base.shippingEnabled,
          footerEnabled: base.footerEnabled,
          footerCols: { ...base.footerCols }
        }, { emitEvent: false });
        this.form.enable({ emitEvent: false });
      }
    });
  }

  get f() { return this.form.controls as any; }

  /** DB -> Form */
  private dbRowToForm(row: any): CheckoutFormShape {
    return {
      cartTitle: row.cart_title ?? '',
      itemListTitle: row.item_list_title ?? '',
      checkoutSideTitle: row.checkout_title ?? '',
      shippingEnabled: !!row.shipping_enabled,
      footerEnabled: !!row.footer_enabled,
      footerCols: {
        col1: { title: row.footer_col1_title ?? '', subtitle: row.footer_col1_subtitle ?? '' },
        col2: { title: row.footer_col2_title ?? '', subtitle: row.footer_col2_subtitle ?? '' },
        col3: { title: row.footer_col3_title ?? '', subtitle: row.footer_col3_subtitle ?? '' },
        col4: { title: row.footer_col4_title ?? '', subtitle: row.footer_col4_subtitle ?? '' },
      }
    };
  }

  private buildApiPayloadFromForm() {
    const v = this.form.value as any
    return {
      cartTitle: v.cartTitle,
      itemListTitle: v.itemListTitle,
      checkoutSideTitle: v.checkoutSideTitle,
      shippingEnabled: !!v.shippingEnabled,
      footerEnabled: !!v.footerEnabled,
      footerCols: {
        col1: { title: v.footerCols.col1.title, subtitle: v.footerCols.col1.subtitle },
        col2: { title: v.footerCols.col2.title, subtitle: v.footerCols.col2.subtitle },
        col3: { title: v.footerCols.col3.title, subtitle: v.footerCols.col3.subtitle },
        col4: { title: v.footerCols.col4.title, subtitle: v.footerCols.col4.subtitle },
      },
      useDefaults: 0
    };
  }

  saveCheckoutTemplate(): void {
    const useDefaults = !!this.form.get('useDefaults')?.value;

    if (useDefaults) {
      const body = { useDefaults: 1 };
      this.loading = true;
      this.lambdaService.upsertCheckoutTemplate(this.themingService.shopId, body)
        .then((res: any) => {
          const ok = res?.status === 200 || res?.statusCode === 200 || res === true;
          if (ok) {
            this.openModal('#checkoutSuccessModal');
          } else {
            this.openModal('#checkoutErrorModal');
          }
        })
        .catch(err => {
          console.error('POST error', err);
          this.openModal('#checkoutErrorModal');
        })
        .finally(() => this.loading = false);
      return;
    }

    this.showErrors = true;
    if (this.f.cartTitle.invalid || this.f.itemListTitle.invalid || this.f.checkoutSideTitle.invalid) {
      this.openModal('#checkoutRequiredModal');
      return;
    }

    const body = this.buildApiPayloadFromForm();
    this.loading = true;
    this.lambdaService.upsertCheckoutTemplate(this.themingService.shopId, body)
      .then((res: any) => {
        const ok = res?.status === 200 || res?.statusCode === 200 || res === true;
        if (ok) {
          this.openModal('#checkoutSuccessModal');
          this.dbSnapshot = {
            cartTitle: body.cartTitle,
            itemListTitle: body.itemListTitle,
            checkoutSideTitle: body.checkoutSideTitle,
            shippingEnabled: body.shippingEnabled,
            footerEnabled: body.footerEnabled,
            footerCols: body.footerCols
          };
        } else {
          this.openModal('#checkoutErrorModal');
        }
      })
      .catch(err => {
        console.error('POST error', err);
        this.openModal('#checkoutErrorModal');
      })
      .finally(() => this.loading = false);
  }

  closeModal() {
    this.hideModal('#checkoutSuccessModal');
    this.hideModal('#checkoutRequiredModal');
    this.hideModal('#checkoutErrorModal');
  }

  private openModal(selector: string) {
    if (typeof $ !== 'undefined') $(selector).modal('show');
  }
  private hideModal(selector: string) {
    if (typeof $ !== 'undefined') $(selector).modal('hide');
  }
}
